import { useRef } from 'react';
import { useToggle } from '@tonic-one/react-hooks';
import {
  Alert,
  AlertCloseButton,
  Box,
  Button,
  Collapse,
  Flex,
  Text,
} from '@tonic-one/react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import V1InstanceApi from '@Apis/V1/instance';
import type { SolutionTrialEligibilityResponse } from '@Types/credit';

const TRIAL_EXPIRING_THRESHOLD_DAYS = 15;
const EMAIL_COLLABORATION_APP_CODE = 'V1_EMAILSECURITY';

interface CreditInAppFeatureWithDrawer {
  openCreditDrawer: (options: {
    el: HTMLElement;
    container: HTMLElement;
    app: string;
  }) => void;
}

const TrialExpiringBanner = () => {
  const { t } = useTranslation();
  const [isOpen, toggleIsOpen] = useToggle(true);
  const elRef = useRef<HTMLDivElement>(null);

  const { data } = useQuery<SolutionTrialEligibilityResponse>({
    queryKey: ['solutionTrialEligibility', EMAIL_COLLABORATION_APP_CODE],
    queryFn: () =>
      V1InstanceApi.getSolutionTrialEligibilityStatus(
        EMAIL_COLLABORATION_APP_CODE
      ),
    staleTime: 5 * 60 * 1000,
    retry: 1,
    refetchOnWindowFocus: false,
  });

  const latestTrial = data?.latestTrial;

  if (!latestTrial || latestTrial.status !== 'Activated') {
    return null;
  }

  const daysLeft = dayjs(latestTrial.endTime).diff(dayjs(), 'day');

  if (daysLeft > TRIAL_EXPIRING_THRESHOLD_DAYS || daysLeft < 0) {
    return null;
  }

  const handleManageTrial = () => {
    const topWindow = window.top as
      | (Window & { creditInAppFeature?: CreditInAppFeatureWithDrawer })
      | null;

    if (elRef.current && topWindow?.creditInAppFeature?.openCreditDrawer) {
      topWindow.creditInAppFeature.openCreditDrawer({
        el: elRef.current,
        container: document.head,
        app: EMAIL_COLLABORATION_APP_CODE,
      });
    }
  };

  return (
    <Collapse in={isOpen} unmountOnExit>
      <Alert
        variant="outline"
        severity="info"
        onClose={() => toggleIsOpen(false)}
        mt={8}
      >
        <Flex alignItems="center" justifyContent="space-between" mr="10x">
          <Text>
            <strong>{t('common.banner.trialExpiringImportant')}</strong>{' '}
            {t('common.banner.trialExpiringMessage', { days: daysLeft })}
          </Text>
          <Button
            variant="secondary"
            size="sm"
            onClick={handleManageTrial}
            mx="4x"
            flexShrink={0}
          >
            {t('common.banner.trialExpiringButton')}
          </Button>
        </Flex>

        <AlertCloseButton
          top={3}
          right={7}
          position="absolute"
          data-test="trial-expiring-banner-close"
        />
      </Alert>
      <Box ref={elRef} />
    </Collapse>
  );
};

export default TrialExpiringBanner;
